import { Home, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useState } from 'react';
import { secureUrl } from '../utils/secureUrl';

interface PropertyCardProps {
  id: string;
  name: string;
  facilitator?: string;
  marketingName?: string;
  city: string;
  province: string;
  propertyType: string;
  imageUrl?: string;
  images?: string[];
  minPrice: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  parking?: number;
  size?: number;
  isNew?: boolean;
  isFeatured?: boolean;
  onClick?: () => void;
}

export default function PropertyCard({
  name,
  facilitator,
  marketingName,
  imageUrl,
  images,
  minPrice,
  isFeatured,
  onClick
}: PropertyCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const isHQDevelopment = facilitator?.toLowerCase().includes('propinvest') ||
                         facilitator?.toLowerCase() === 'hq' ||
                         !facilitator ||
                         facilitator?.trim() === '';

  const displayName = (isHQDevelopment && marketingName) ? marketingName : name;

  const displayImages = images && images.length > 0 ? images : (imageUrl ? [imageUrl] : []);
  const hasMultipleImages = displayImages.length > 1;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 cursor-pointer group hover-lift"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-gray-200 overflow-hidden">
        {isFeatured && (
          <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md">
            <Star size={10} className="fill-white" />
            Featured
          </div>
        )}
        {displayImages.length > 0 ? (
          <>
            <img
              src={secureUrl(displayImages[currentImageIndex])}
              alt={displayName}
              width={400}
              height={300}
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            {hasMultipleImages && (
              <>
                <button
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                  className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft size={18} className="text-gray-800" />
                </button>
                <button
                  onClick={handleNextImage}
                  aria-label="Next image"
                  className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight size={18} className="text-gray-800" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {displayImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative">
            <div className="absolute inset-0 opacity-[0.05]">
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gray-900" />
              <div className="absolute -left-4 -bottom-4 w-20 h-20 rounded-full bg-gray-700" />
            </div>
            <div className="relative w-16 h-16 rounded-2xl bg-white shadow flex items-center justify-center">
              <Home className="w-8 h-8 text-gray-300" />
            </div>
          </div>
        )}
      </div>

      {/* Green Label Bar */}
      <div className="green-label-bar">
        <h3 className="text-sm md:text-base font-bold truncate">{displayName}</h3>
        <p className="text-xs text-white/90 mt-0.5">
          Investments from {formatPrice(minPrice)}
        </p>
      </div>
    </div>
  );
}
